// Today (concept A, refined): the day at a glance and what needs you.
//   Header: the date and how many moves need you (the shell's AI button, top right, is Quick capture here).
//   Four cards (6.3): due this week, meetings this week, awaiting approval, knowledge base (desktop only).
//   Today's schedule: an hour strip with meetings, reminders and due items (desktop).
//   Needs you (from the assistant, yours, gone quiet) beside Reminders and due (Today and Tomorrow).
//   This week: seven days, filtered by type.
// Everything opens a drawer (a sheet from the bottom on a phone) with the way through to its page.
import { useAiScope } from '../assistant/AiButton';
import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import type { Reminder, SessionUser, TeamCheck, TodayItem } from '@dpw/shared';
import { api } from '../api';
import { useActions, useChecksToAnswer, useReminders, useToday } from '../lib/queries';
import { useNow, usePhone } from '../lib/media';
import { AddReminderForm } from '../ui/AddReminderForm';
import { ErrorNote } from '../ui/bits';
import { Glide, Skel, SkelList } from '../ui/motion';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/toast';
import { TodayDrawer, type DrawerState } from './today/Drawer';
import { NeedsYou } from './today/NeedsYou';
import { RemindersDue } from './today/RemindersDue';
import { Schedule } from './today/Schedule';
import { Week } from './today/Week';
import { TodayCards } from './today/Cards';
import { longDay, remindersFor } from './today/util';
import { PhoneToday, PhoneTodaySkeleton } from '../phone/Today';

function TodaySkeleton() {
  return (
    <div className="today" role="status" aria-label="Loading">
      <div className="hdr"><Skel w="38%" h={30} r={8} /></div>
      <div className="pnl sched"><div className="sech"><Skel w="40%" h={14} /></div><div style={{ padding: 18 }}><Skel h={110} r={12} /></div></div>
      <div className="tg">
        <div className="nyc"><div className="sech"><Skel w="30%" h={14} /></div><div style={{ padding: 18 }}><SkelList rows={4} variant="conv" /></div></div>
        <div className="rmc"><div className="sech"><Skel w="30%" h={14} /></div><div style={{ padding: 18 }}><SkelList rows={4} variant="conv" /></div></div>
      </div>
    </div>
  );
}

export function Today({ me }: { me: SessionUser }) {
  // Today's AI button is Quick capture: a new conversation for "what happened", in the same drawer.
  useAiScope({ capture: true, purpose: 'capture' });
  const q = useToday();
  const rem = useReminders('mine');
  const acts = useActions('waiting');
  const asks = useChecksToAnswer();
  const now = useNow();
  const phone = usePhone();
  const qc = useQueryClient();
  const toast = useToast();
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [adding, setAdding] = useState(false);
  const [counts, setCounts] = useState({ open: 0, cleared: 0 });
  const onCount = useCallback((open: number, cleared: number) => setCounts(c => (c.open === open && c.cleared === cleared ? c : { open, cleared })), []);

  // Ticking a reminder: at once, rolled back if the server says no.
  const toggle = (r: Reminder) => {
    const key = ['reminders', 'mine'];
    const before = qc.getQueryData<Reminder[]>(key);
    const done = r.state !== 'done';
    qc.setQueryData<Reminder[]>(key, l => l?.map(x => (x.id === r.id ? { ...x, state: done ? 'done' : 'today', doneAt: done ? new Date().toISOString() : null } : x)));
    (done ? api.reminders.done(r.id) : api.reminders.undo(r.id)).then(
      () => void qc.invalidateQueries(),
      (e: unknown) => { qc.setQueryData(key, before); toast(e instanceof Error ? e.message : 'Could not change it.'); },
    );
  };

  const items = useMemo(() => {
    const m = new Map<string, TodayItem>();
    for (const x of [...(q.data?.meetings ?? []), ...(q.data?.due.today ?? []), ...(q.data?.due.tomorrow ?? []), ...(q.data?.week ?? []).flatMap(d => d.items)]) m.set(x.id, x);
    return [...m.values()];
  }, [q.data]);

  // Both count what needs you in the title and list the assistant's cards in Needs you, so both wait for the cards and
  // questions too: rendering first said "Nothing from the assistant" and then pushed every move down under the
  // pointer when they arrived (a click on "I will call" landed on empty space).
  if (q.isLoading || acts.isLoading || asks.isLoading) return phone ? <PhoneTodaySkeleton /> : <TodaySkeleton />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const t = q.data;
  const reminders = rem.data ?? [];
  const checks = (asks.data ?? []).filter((c): c is TeamCheck => c.view === 'full' && c.status === 'open');
  if (phone) return <PhoneToday me={me} t={t} reminders={reminders} actions={acts.data ?? []} checks={checks} onToggle={toggle} />;
  const side = 'right';

  return (
    <Glide className="today">
      <div className="hdr">
        <div className="htl">
          <h1>{longDay(t.date)}. <span className="mut" style={{ fontWeight: 500 }}>{counts.open ? `${counts.open} ${counts.open === 1 ? 'move needs' : 'moves need'} you.` : 'You are clear.'}</span></h1>
        </div>
      </div>
      <TodayCards />
      <Schedule meetings={t.meetings} due={t.due.today} reminders={remindersFor(reminders, 'today', t.date).filter(r => r.state !== 'overdue')} now={now} onOpen={setDrawer} />
      <div className="tg">
        <NeedsYou me={me} actions={acts.data ?? []} checks={checks} yours={t.yours} quiet={t.quiet} onCount={onCount} />
        <RemindersDue day={day} setDay={setDay} reminders={reminders} due={t.due} today={t.date} onToggle={toggle} onAdd={() => setAdding(true)} onOpen={setDrawer} />
      </div>
      <Week week={t.week} today={t.date} phone={false} onOpen={setDrawer} />
      <AnimatePresence>
        {drawer && <TodayDrawer key="drawer" state={drawer} items={items} reminders={reminders} today={t.date} phone={false} onClose={() => setDrawer(null)} onOpen={setDrawer}
          onDone={r => { toggle(r); setDrawer(null); toast('Reminder done'); }} />}
        {adding && (
          <Sheet key="add" side={side} width={620} label="Add a reminder" onClose={() => setAdding(false)} crumbs={<><span>Today</span><span>/</span><b>Add a reminder</b></>}>
            <AddReminderForm me={me} autoFocus onSaved={() => setAdding(false)} />
          </Sheet>
        )}
      </AnimatePresence>
    </Glide>
  );
}
